import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SEO } from "@/components/SEO";
import SocialShare from "@/components/SocialShare";
import Comments from "@/components/Comments";
import ArticleCard from "@/components/ArticleCard";
import Paywall from "@/components/Paywall";
import { AuthorAvatar } from "@/components/AuthorAvatar";
import { FacebookCard } from "@/components/FacebookCard";
import { Clock, Eye, Volume2, Lock } from "lucide-react";

function isFacebookUrl(u) {
  return typeof u === "string" && u.includes("facebook.com");
}

function toEmbedUrl(url) {
  if (!url) return { src: "", isFacebook: false };
  if (url.includes("facebook.com")) {
    // Universal FB plugin — handles reels, videos, posts, photos
    const src = `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(url)}&show_text=false&width=700`;
    return { src, isFacebook: true };
  }
  if (url.includes("/embed/")) return { src: url, isFacebook: false };
  const m = url.match(/(?:v=|youtu\.be\/)([\w-]{11})/);
  return { src: m ? `https://www.youtube.com/embed/${m[1]}` : url, isFacebook: false };
}

export default function Article() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    window.scrollTo({ top: 0 });
    const email = localStorage.getItem("ittv_sub_email") || undefined;
    api.get(`/articles/${slug}`, { params: email ? { email } : {} })
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-editorial py-24 text-center">
        <h1 className="font-serif text-5xl font-bold">404</h1>
        <p className="mt-4 text-[color:var(--text-muted)]">This story could not be found.</p>
        <Link to="/" className="btn-outline inline-block mt-6">Back to front page</Link>
      </div>
    );
  }

  if (!data) return <div className="container-editorial py-24 text-center text-[color:var(--text-muted)]">Loading…</div>;

  const { article, related } = data;
  const paras = article.content.split(/\n\n+/);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "mainEntityOfPage": { "@type": "WebPage", "@id": typeof window !== "undefined" ? window.location.href : "" },
    headline: article.title,
    alternativeHeadline: article.excerpt,
    description: article.excerpt,
    image: [article.cover_image],
    datePublished: article.created_at,
    dateModified: article.updated_at,
    author: [{
      "@type": "Person",
      name: article.author_name,
      url: `https://ittvnews.com/author/${article.author_id}`,
    }],
    publisher: {
      "@type": "NewsMediaOrganization",
      name: "ITTV News",
      logo: { "@type": "ImageObject", url: "https://ittvnews.com/logo.png" },
      url: "https://ittvnews.com",
    },
    keywords: (article.tags || []).join(", "),
    articleSection: article.category_name,
    inLanguage: "en",
    isAccessibleForFree: !article.premium,
    ...(article.premium && {
      hasPart: {
        "@type": "WebPageElement",
        isAccessibleForFree: false,
        cssSelector: "[data-testid='article-body']",
      },
    }),
  };

  return (
    <>
      <SEO
        title={`${article.title} · ITTV News`}
        description={article.excerpt}
        image={article.cover_image}
        type="article"
        jsonLd={jsonLd}
      />

      <article className="reveal">
        {/* Header */}
        <div className="container-editorial pt-10 pb-8 md:pt-14 md:pb-12">
          <div className="max-w-3xl mx-auto">
            <Link to={`/category/${article.category_slug}`} className="kicker text-[color:var(--accent)] hover-underline" data-testid="article-category">
              ← {article.category_name}
            </Link>
            {article.premium && (
              <span className="ml-3 inline-flex items-center gap-1 kicker bg-[color:var(--accent)] text-white px-2 py-1" data-testid="premium-badge">
                <Lock size={11} /> Subscribers
              </span>
            )}
            <h1 className="font-serif text-4xl md:text-6xl font-bold leading-[0.98] tracking-tight mt-4" data-testid="article-title">
              {article.title}
            </h1>
            <p className="mt-6 text-xl md:text-2xl text-[color:var(--text-muted)] font-serif italic leading-snug" data-testid="article-excerpt">
              {article.excerpt}
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 pb-6 border-b" style={{ borderColor: "hsl(var(--border-hsl))" }}>
              <Link to={`/author/${article.author_id}`} className="flex items-center gap-3">
                <AuthorAvatar name={article.author_name} src={article.author_avatar} size="md" />
                <div>
                  <div className="font-semibold">{article.author_name}</div>
                  <div className="text-xs text-[color:var(--text-muted)]">
                    {new Date(article.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })} · <Clock size={12} className="inline" /> {article.read_time} min · <Eye size={12} className="inline" /> {article.views}
                  </div>
                </div>
              </Link>
              <SocialShare url={typeof window !== "undefined" ? window.location.href : ""} title={article.title} />
            </div>
          </div>
        </div>

        {/* Cover / Media */}
        <div className="container-editorial mb-10">
          {article.media_type === "video" && article.media_url ? (
            isFacebookUrl(article.media_url) ? (
              <div className="w-full max-w-3xl mx-auto" data-testid="video-embed">
                <FacebookCard url={article.media_url} label={article.title} cover={article.cover_image} />
              </div>
            ) : (
              <div className="aspect-video w-full max-w-6xl mx-auto" data-testid="video-embed">
                <iframe
                  className="w-full h-full"
                  src={toEmbedUrl(article.media_url).src}
                  title={article.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )
          ) : (
            <img src={article.cover_image} alt={article.title} className="w-full max-w-6xl mx-auto max-h-[70vh] object-cover" data-testid="article-cover" />
          )}
          {article.media_type === "audio" && article.media_url && (
            <div className="max-w-3xl mx-auto mt-6 p-6 border" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="audio-player">
              <div className="flex items-center gap-2 mb-3">
                <Volume2 className="text-[color:var(--accent)]" />
                <span className="kicker">Listen to this dispatch</span>
              </div>
              <audio controls className="w-full">
                <source src={article.media_url} />
              </audio>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="container-editorial pb-12">
          <div className="max-w-3xl mx-auto article-prose" data-testid="article-body">
            {paras.map((p, i) => <p key={i}>{p}</p>)}
          </div>

          {article.is_locked && <Paywall />}

          {/* Tags */}
          {article.tags?.length > 0 && (
            <div className="max-w-3xl mx-auto mt-10 flex flex-wrap gap-2">
              {article.tags.map((t) => (
                <Link key={t} to={`/search?q=${encodeURIComponent(t)}`} className="border px-3 py-1.5 kicker hover:bg-[color:var(--text)] hover:text-[color:var(--bg)] transition-colors" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid={`article-tag-${t}`}>
                  #{t}
                </Link>
              ))}
            </div>
          )}

          {/* Author card */}
          <div className="max-w-3xl mx-auto mt-14 border p-8 flex flex-col md:flex-row gap-6 bg-[color:var(--surface)]" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="author-card">
            <div className="w-24 shrink-0">
              <AuthorAvatar name={article.author_name} src={article.author_avatar} size="lg" />
            </div>
            <div>
              <div className="kicker text-[color:var(--accent)] mb-2">Written by</div>
              <h4 className="font-serif text-2xl font-bold">{article.author_name}</h4>
              <p className="text-[color:var(--text-muted)] mt-2">Julia Fosah is the founding editor of ITTV News, reporting on politics, media and culture across three continents.</p>
              <Link to={`/author/${article.author_id}`} className="btn-outline inline-block mt-4">Read her work</Link>
            </div>
          </div>
        </div>

        {/* Comments */}
        <div className="container-editorial border-t" style={{ borderColor: "hsl(var(--border-hsl))" }}>
          <Comments articleId={article.id} />
        </div>

        {/* Related */}
        {related?.length > 0 && (
          <div className="bg-[color:var(--surface)] border-y" style={{ borderColor: "hsl(var(--border-hsl))" }}>
            <div className="container-editorial py-16">
              <div className="kicker text-[color:var(--accent)] mb-2">Keep reading</div>
              <h3 className="font-serif text-4xl font-bold mb-8 tracking-tight">More from {article.category_name}</h3>
              <div className="grid md:grid-cols-4 gap-8">
                {related.map((r) => <ArticleCard key={r.slug} article={r} />)}
              </div>
            </div>
          </div>
        )}
      </article>
    </>
  );
}
